import type {
  PublicCommunityShopsSearchQuery,
  PublicCommunityShopsSearchResponse,
  PublicCommunityCountriesResponse,
  PublicCommunityHighlightedSuppliersResponse,
  PublicCommunityShopProductsQuery,
  PublicCommunityShopProductsResponse,
} from "./types";

const API_BASE = `${process.env.NEXT_PUBLIC_BUDDIFY_API_URL ?? "http://localhost:3330"}/public/community`;

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Community API error: ${response.status}`);
  }

  const envelope = (await response.json()) as {
    ok: boolean;
    error: { message: string } | null;
    data: T;
  };

  if (!envelope.ok || envelope.error) {
    throw new Error(envelope.error?.message || "Community API returned an error");
  }

  return envelope.data;
}

export async function fetchCommunityCountries(): Promise<PublicCommunityCountriesResponse> {
  return fetchJson<PublicCommunityCountriesResponse>(`${API_BASE}/countries`);
}

export async function fetchCommunityHighlightedSuppliers(): Promise<PublicCommunityHighlightedSuppliersResponse> {
  return fetchJson<PublicCommunityHighlightedSuppliersResponse>(
    `${API_BASE}/highlighted-suppliers`,
  );
}

export async function searchCommunityShops(
  query: PublicCommunityShopsSearchQuery,
): Promise<PublicCommunityShopsSearchResponse> {
  const params = new URLSearchParams();

  if (query.search_query) {
    params.set("search_query", query.search_query);
  }
  if (query.filter_category_internal_id) {
    params.set("filter_category_internal_id", query.filter_category_internal_id);
  }
  if (query.filter_featured_collection_internal_id) {
    params.set(
      "filter_featured_collection_internal_id",
      query.filter_featured_collection_internal_id,
    );
  }
  if (query.filter_ships_from) {
    params.set("filter_ships_from", query.filter_ships_from);
  }
  if (query.filter_sort_by) {
    params.set("filter_sort_by", query.filter_sort_by);
  }
  if (typeof query.take === "number") {
    params.set("take", String(query.take));
  }
  params.set("skip", String(query.skip ?? 0));

  return fetchJson<PublicCommunityShopsSearchResponse>(
    `${API_BASE}/shops/search?${params.toString()}`,
  );
}

export async function fetchShopProducts(
  slug: string,
  query: PublicCommunityShopProductsQuery,
): Promise<PublicCommunityShopProductsResponse> {
  const params = new URLSearchParams();

  if (query.search_query) {
    params.set("search_query", query.search_query);
  }
  if (query.sort_by) {
    params.set("sort_by", query.sort_by);
  }
  if (typeof query.take === "number") {
    params.set("take", String(query.take));
  }
  params.set("skip", String(query.skip ?? 0));

  return fetchJson<PublicCommunityShopProductsResponse>(
    `${API_BASE}/shops/${encodeURIComponent(slug)}/products?${params.toString()}`,
  );
}
